import { create } from 'zustand';
import { StoreApi, UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { entityService } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';
import { NoteEntity } from '../types/note';
import useEntityStore from '../../../stores/entityStore';

const log = createFileLogger('NOTES_APP_STORE', { enabled: false });

export type NotesView = 'list' | 'editor';

interface NotesAppState {
  windowId: string;
  spaceId: string;
  
  currentView: NotesView;
  selectedNoteId?: string;
  notes: NoteEntity[];
  searchQuery: string;
  
  isLoading: boolean;
  isInitialized: boolean;
  error?: string;
}

export interface NotesAppStore extends NotesAppState {
  initialize: (winId: string, spcId: string) => Promise<void>;
  loadNotes: () => Promise<void>;
  createNote: (title?: string) => Promise<NoteEntity>;
  deleteNote: (noteId: string) => Promise<void>;
  selectNote: (noteId?: string) => void;
  navigateToView: (view: NotesView) => void;
  searchNotes: (query: string) => void;
  updateNoteTitle: (noteId: string, title: string) => Promise<void>;
}

export type NotesAppStoreHook = UseBoundStore<StoreApi<NotesAppStore>>;

const createNotesAppStore = (windowId: string, spaceId: string): NotesAppStoreHook => {
  let unsubscribeEntityStore: (() => void) | null = null;
  
  const storeCreated = create<NotesAppStore>()(
    subscribeWithSelector((set, get) => ({
      windowId,
      spaceId,
      
      currentView: 'list' as NotesView,
      selectedNoteId: undefined,
      notes: [],
      searchQuery: '',
      
      isLoading: false,
      isInitialized: false,
      error: undefined,
      
      initialize: async (winId: string, spcId: string) => {
        log.info('[NotesApp] Initializing...', { windowId: winId, spaceId: spcId });
        
        try {
          set({ isLoading: true, error: undefined });
          
          const params = new URLSearchParams(window.location.search);
          const urlNoteId = params.get('noteId') || undefined;
          const urlView = (params.get('view') as NotesView) || 'list';
          const urlSearch = params.get('search') || '';
          
          log.info('[NotesApp] URL on startup:', { urlNoteId, urlView, urlSearch });
          
          await get().loadNotes();
          
          // Subscribe to entity store changes for reactive updates
          unsubscribeEntityStore = useEntityStore.subscribe((state: any, prevState: any) => {
            // Only process if entities actually changed
            if (state.entities === prevState.entities) return;
            
            const currentSpaceId = get().spaceId;
            const currentNotes = get().notes;
            
            // Filter notes for current space (exclude trashed)
            const updatedNotes = state.entities.filter(
              (e: any) => e.entityType === 'Note' && e.spaceId === currentSpaceId && !e.trashed
            ) as NoteEntity[];
            
            // Check if notes actually changed
            const notesChanged = updatedNotes.length !== currentNotes.length ||
              updatedNotes.some((note: NoteEntity) => {
                const currentNote = currentNotes.find(n => (n._id || n.id) === (note._id || note.id));
                return !currentNote || 
                  currentNote.name !== note.name ||
                  currentNote.skeleton?.updatedAt !== note.skeleton?.updatedAt;
              });
            
            if (notesChanged) {
              const sortedNotes = updatedNotes.sort((a, b) => 
                new Date(b.skeleton?.updatedAt || b.updated_at || 0).getTime() - 
                new Date(a.skeleton?.updatedAt || a.updated_at || 0).getTime()
              );
              
              log.info('[NotesApp] Notes updated from entity store:', { count: sortedNotes.length });
              set({ notes: sortedNotes });
            }
          });
          
          set({
            currentView: urlNoteId ? 'editor' : urlView,
            selectedNoteId: urlNoteId,
            searchQuery: urlSearch,
            isInitialized: true,
            isLoading: false
          });
          
          log.info('[NotesApp] Initialized successfully');
        } catch (error) {
          log.error('[NotesApp] Initialization failed:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to initialize',
            isLoading: false,
            isInitialized: true
          });
        }
      },
      
      loadNotes: async () => {
        try {
          log.info('[NotesApp] Loading notes...', { spaceId: get().spaceId });
          
          let notes: NoteEntity[];
          
          // Check if we're in Electron and have local entity storage
          if ((window as any).galOS?.entities) {
            log.info('[NotesApp] Using local entity storage (Electron)');
            const userId = 'default-user'; // You may want to get this from auth context
            const allNotes = await (window as any).galOS.entities.getEntities(userId, 'note', {}) as NoteEntity[];
            // Filter out trashed notes
            notes = allNotes.filter(n => !n.trashed);
          } else {
            // ✅ FIX: Use entityStore which is space-aware and already has notes loaded
            // This ensures we get notes from the current space, not all notes globally
            const entityStore = useEntityStore.getState();
            const allNotes = entityStore.entities.filter(
              (e: any) => e.entityType === 'Note' && e.spaceId === get().spaceId && !e.trashed
            ) as NoteEntity[];
            
            if (allNotes.length > 0) {
              log.info('[NotesApp] Using notes from entityStore:', { count: allNotes.length });
              notes = allNotes;
            } else {
              // Fallback to query if entityStore doesn't have notes yet
              log.info('[NotesApp] EntityStore empty, querying via entity service');
              const queriedNotes = await entityService.queryEntities({
                entityType: 'Note',
                spaceId: get().spaceId
              }) as NoteEntity[];
              // Filter out trashed notes
              notes = queriedNotes.filter(n => !n.trashed);
            }
          }
          
          const sortedNotes = notes.sort((a, b) => 
            new Date(b.skeleton?.updatedAt || b.updated_at || 0).getTime() - 
            new Date(a.skeleton?.updatedAt || a.updated_at || 0).getTime()
          );
          
          set({ notes: sortedNotes });
          
          log.info('[NotesApp] Loaded notes:', { count: sortedNotes.length, spaceId: get().spaceId });
        } catch (error) {
          log.error('[NotesApp] Failed to load notes:', error);
          set({ error: 'Failed to load notes' });
        }
      },
      
      createNote: async (title?: string) => {
        try {
          log.info('[NotesApp] Creating note...', { title });
          
          const now = new Date().toISOString();
          const noteTitle = title || 'Untitled Note';
          
          // ✅ CORRECT: Use entityActions (follows architecture)
          // This handles: backend call, CRDT sync, undo/redo, events, normalization
          // The entityStore subscription will automatically update our notes list
          const note = await useEntityStore.getState().actions.createEntity({
            entityType: 'Note',
            name: noteTitle,
            spaceId: get().spaceId,
            skeleton: {
              '@type': 'Note',
              preview: '',
              createdAt: now,
              updatedAt: now
            }
          }) as NoteEntity;
          
          // Update local UI state (selection)
          set({
            selectedNoteId: note._id || note.id,
            currentView: 'editor'
          });
          
          log.info('[NotesApp] ✅ Note created via entityActions:', { id: note._id || note.id });
          return note;
        } catch (error) {
          log.error('[NotesApp] Failed to create note:', error);
          throw error;
        }
      },
      
      deleteNote: async (noteId: string) => {
        try {
          log.info('[NotesApp] Deleting note...', { noteId });
          
          // ✅ CORRECT: Use entityActions (follows architecture)
          // This handles: backend call, CRDT sync, undo/redo, events, normalization
          // The entityStore subscription will automatically update our notes list
          await useEntityStore.getState().actions.softDeleteEntity(noteId);
          
          // Update local UI state (selection)
          set(state => {
            const selectedNoteId = state.selectedNoteId === noteId 
              ? undefined 
              : state.selectedNoteId;
            const currentView = selectedNoteId ? 'editor' : 'list';
            
            return { selectedNoteId, currentView };
          });
          
          log.info('[NotesApp] ✅ Note deleted via entityActions');
        } catch (error) {
          log.error('[NotesApp] Failed to delete note:', error);
          throw error;
        }
      },
      
      selectNote: (noteId?: string) => {
        log.info('[NotesApp] Selecting note:', { noteId });
        set({ 
          selectedNoteId: noteId,
          currentView: noteId ? 'editor' : 'list'
        });
      },
      
      navigateToView: (view: NotesView) => {
        log.info('[NotesApp] Navigating to view:', { view });
        set({ currentView: view });
      },
      
      searchNotes: (query: string) => {
        log.info('[NotesApp] Searching notes:', { query });
        set({ searchQuery: query });
      },
      
      updateNoteTitle: async (noteId: string, title: string) => {
        try {
          const updatedAt = new Date().toISOString();
          
          log.info('[NotesApp] Updating note title via entityActions');
          
          // ✅ CORRECT: Use entityActions (follows architecture)
          // This handles: backend call, CRDT sync, undo/redo, events, normalization
          // The entityStore subscription will automatically update our notes list
          await useEntityStore.getState().actions.updateEntity(noteId, {
            name: title,
            skeleton: {
              updatedAt
            }
          });
          
          log.info('[NotesApp] ✅ Note title updated via entityActions');
        } catch (error) {
          log.error('[NotesApp] Failed to update note title:', error);
        }
      }
    }))
  );
  
  const store = storeCreated;
  
  store.subscribe(
    (state: NotesAppStore) => ({
      isInitialized: state.isInitialized,
      currentView: state.currentView,
      selectedNoteId: state.selectedNoteId,
      searchQuery: state.searchQuery
    }),
    (routingState: { isInitialized: boolean; currentView: NotesView; selectedNoteId?: string; searchQuery: string }) => {
      if (!routingState.isInitialized) return;
      
      const params = new URLSearchParams();
      
      if (routingState.selectedNoteId) {
        params.set('noteId', routingState.selectedNoteId);
      }
      if (routingState.currentView !== 'list' && !routingState.selectedNoteId) {
        params.set('view', routingState.currentView);
      }
      if (routingState.searchQuery) {
        params.set('search', routingState.searchQuery);
      }
      
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
  );
  
  const handlePopState = () => {
    const state = store.getState();
    
    if (!state.isInitialized) return;
    
    const params = new URLSearchParams(window.location.search);
    const urlNoteId = params.get('noteId') || undefined;
    const urlView = (params.get('view') as NotesView) || 'list';
    const urlSearch = params.get('search') || '';
    
    log.info('[NotesApp] Browser navigation detected:', { urlNoteId, urlView, urlSearch });
    
    if (urlNoteId !== state.selectedNoteId) {
      state.selectNote(urlNoteId);
    } else if (urlView !== state.currentView) {
      state.navigateToView(urlView);
    }
    if (urlSearch !== state.searchQuery) {
      state.searchNotes(urlSearch);
    }
  };
  
  window.addEventListener('popstate', handlePopState);
  
  // Store cleanup function
  const key = `${windowId}-${spaceId}`;
  cleanupMap.set(key, () => {
    if (unsubscribeEntityStore) {
      unsubscribeEntityStore();
      unsubscribeEntityStore = null;
    }
    window.removeEventListener('popstate', handlePopState);
  });
  
  return storeCreated;
};

const storeMap = new Map<string, NotesAppStoreHook>();
const cleanupMap = new Map<string, () => void>();

export const getNotesAppStore = (windowId: string, spaceId: string): NotesAppStoreHook => {
  const key = `${windowId}-${spaceId}`;
  if (!storeMap.has(key)) {
    storeMap.set(key, createNotesAppStore(windowId, spaceId));
  }
  return storeMap.get(key)!;
};

export const destroyNotesAppStore = (windowId: string, spaceId: string): void => {
  const key = `${windowId}-${spaceId}`;
  const cleanup = cleanupMap.get(key);
  if (cleanup) {
    cleanup();
    cleanupMap.delete(key);
  }
  storeMap.delete(key);
};

export const useNotesAppStore = (windowId: string, spaceId: string): NotesAppStore => {
  const store = getNotesAppStore(windowId, spaceId);
  return store();
};

